<template>
    <base-dialog :loading="loading" :value="dialog" :width="780" :fullscreen="isMobile" :title="$t('programs.assign_program')"
        @input="val => { if (!val) hide() }" @close="hide">
        <div class="asg-grid">
            <div class="asg-left">
                <set-selection-routine :coach-id="coachId" @change="onChangeSelection"
                    @collection="collection = $event" ref="selection" />
                <div class="asg-field">
                    <label class="asg-label">{{ $t('programs.days_to_assign') }}</label>
                    <weekday-picker v-model="weekdays" />
                </div>
                <div class="asg-warn">
                    <v-icon size="16" color="#D97706">mdi-alert-outline</v-icon>
                    <span>{{ $t('programs.alert_replace_routines') }}</span>
                </div>
            </div>
            <div class="asg-right">
                <div class="asg-hint">{{ $t('programs.select_day_to_start_assign') }}</div>
                <mini-calendar :value="startDate" @change="startDate = $event" single force-value />
                <div class="mt-4">
                    <kimsa-combobox :label="$t('routines.destination_routine')" dense :items="routineOptions"
                        :value="routineDestiny" @change="routineDestiny = $event" no-object ref="routineDestiny" />
                </div>
            </div>
        </div>

        <template v-slot:footer>
            <dialog-actions hide-cancel :confirm-label="$t('common.assign')" :disabled="disabled" :loading="loading"
                @confirm="assign" />
        </template>
    </base-dialog>
</template>

<script>

import { db } from "@/utils/Firebase";
import { mapState } from "vuex";
import SetSelectionRoutine from './SetSelectionRoutine.vue';
import MiniCalendar from "@/components/common/MiniCalendar";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import BaseDialog from "@/components/common/BaseDialog";
import DialogActions from "@/components/common/DialogActions";
import { assignProgramDates, programSessionDates } from "@/utils/Exercises";
import WeekdayPicker from "@/components/common/WeekdayPicker";

export default {
    name: "AssignProgramDialog",
    components: { SetSelectionRoutine, MiniCalendar, KimsaCombobox, BaseDialog, DialogActions, WeekdayPicker },
    props: {
        programId: {
            type: String,
            default: null,
        },
        coachId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            dialog: false,
            program: null,
            loading: false,
            currentSelection: null,
            collection: null,
            startDate: null,
            routineDestiny: 1,
            weekdays: [],
        }
    },
    computed: {
        ...mapState(['libraryCoach']),
        routineOptions(vm = this) {
            return [1, 2].map(value => ({ name: `${vm.$t('common.routine')} ${value}`, value }))
        },

        assignedCoachId(vm = this) {
            if (!vm.isCompany) return vm.currentUser?.uid || null
            return vm.libraryCoach?.id || vm.coachId || null
        },
        disabled(vm = this) {
            return !vm.currentSelection || !vm.startDate || vm.weekdays.length === 0
        },
        routinesToCopy(vm = this) {
            return vm.program?.assignedRoutines || []
        },
        routineQuantity(vm = this) {
            return vm.routinesToCopy?.length || 0
        },
        isGroup(vm = this) {
            return vm.collection === 'groupAssignedRoutines'
        },
    },
    methods: {
        async show() {
            const vm = this
            vm.dialog = true
            await vm.loadProgram()
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        reset() {
            const vm = this
            vm.program = null
            if (vm.$refs.selection) vm.$refs.selection.reset()
            vm.collection = null
            vm.startDate = null
            vm.routineDestiny = 1
            if (vm.$refs.routineDestiny) vm.$refs.routineDestiny.model = 1
            vm.weekdays = []
        },
        async loadProgram() {
            let vm = this
            if (!vm.programId) return
            let programRef = await db.collection('programs').doc(vm.programId).get()
            vm.program = { ...programRef.data(), id: programRef.id }
        },
        onChangeSelection(selection) {
            const vm = this;
            vm.currentSelection = selection
        },
        daysToCopy() {
            const vm = this;
            return programSessionDates(vm.startDate, vm.weekdays, vm.routineQuantity)
        },
        async assign() {
            const vm = this
            if (!vm.collection) return vm.toast(vm.$t('common.an_error_has_occurred'), 'error')
            if (!vm.assignedCoachId) return vm.toast(vm.$t('statistics.select_coach_placeholder'), 'error')
            let currentSelection = Array.isArray(vm.currentSelection) ? vm.currentSelection : [vm.currentSelection]
            const collection = vm.collection
            const assignedCoachId = vm.assignedCoachId
            const program = vm.program
            const routineNumber = vm.routineDestiny
            await vm.withLoading(async () => {
            await assignProgramDates({
                program,
                coachId: assignedCoachId,
                collection,
                selections: currentSelection,
                dates: vm.daysToCopy(),
                routineNumber,
            })

            vm.hide()
            vm.toast(vm.$t('programs.assigned_program'))
            })
        },
    },
    watch: {

        programId() {
            this.loadProgram()
        },
    }
}
</script>

<style scoped>
.asg-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin: -20px;
}

.asg-left {
    padding: 24px;
    border-right: 1px solid var(--v-outline-base);
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.asg-right {
    padding: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.asg-hint {
    font-size: 11px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
    letter-spacing: .06em;
    text-align: center;
    margin-bottom: 8px;
}

.asg-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
    text-transform: uppercase;
    letter-spacing: .04em;
    margin-bottom: 8px;
}

.asg-warn {
    margin-top: auto;
    display: flex;
    gap: 10px;
    align-items: flex-start;
    background: rgba(217, 119, 6, .08);
    border: 1px solid rgba(217, 119, 6, .35);
    border-radius: 8px;
    padding: 12px 14px;
    font-size: 12px;
    color: #D97706;
    line-height: 1.5;
}

@media (max-width: 768px) {
    .asg-grid {
        grid-template-columns: 1fr;
    }

    .asg-left {
        border-right: none;
        border-bottom: 1px solid var(--v-outline-base);
    }
}
</style>